import React from 'react';
import { FaCheck, FaExclamationTriangle, FaSpinner } from 'react-icons/fa';

const STEPS = [
    { key: 'saving', label: 'Completing job...' },
    { key: 'invoice', label: 'Creating invoice...' },
    { key: 'rcti', label: 'Creating RCTI...' },
];

/**
 * CompletionProgressModal - Shows a stepper with progress during admin job completion.
 *
 * Props:
 * - isOpen: boolean
 * - currentStep: 'saving' | 'invoice' | 'rcti' | 'done'
 * - stepResults: { saving?: {status, message?}, invoice?: {status, message?, invoiceId?}, rcti?: {status, message?, rctiId?} }
 *   status: 'pending' | 'in-progress' | 'success' | 'failed' | 'skipped'
 * - onClose: function (only enabled when done)
 */
const CompletionProgressModal = ({ isOpen, currentStep, stepResults = {}, warnings = [], onClose }) => {
    if (!isOpen) return null;

    const isDone = currentStep === 'done';
    const hasErrors = Object.values(stepResults).some(r => r?.status === 'failed');

    const getStepStatus = (stepKey) => {
        return stepResults[stepKey]?.status || 'pending';
    };

    const getStepIcon = (stepKey) => {
        const status = getStepStatus(stepKey);
        switch (status) {
            case 'in-progress':
                return <FaSpinner className="animate-spin text-green-600" />;
            case 'success':
                return <FaCheck className="text-green-600" />;
            case 'failed':
                return <FaExclamationTriangle className="text-red-500" />;
            case 'skipped':
                return <span className="text-gray-400">—</span>;
            default:
                return <span className="w-4 h-4 rounded-full border-2 border-gray-300 inline-block" />;
        }
    };

    const getStepTextClass = (stepKey) => {
        const status = getStepStatus(stepKey);
        switch (status) {
            case 'in-progress':
                return 'text-gray-900 font-medium';
            case 'success':
                return 'text-green-700';
            case 'failed':
                return 'text-red-600 font-medium';
            case 'skipped':
                return 'text-gray-400';
            default:
                return 'text-gray-400';
        }
    };

    const getStepLabel = (step) => {
        const status = getStepStatus(step.key);
        const result = stepResults[step.key];

        if (status === 'success') {
            if (step.key === 'saving') return 'Job completed';
            if (step.key === 'invoice' && result?.invoiceId) return `Invoice created (${result.invoiceId})`;
            if (step.key === 'rcti' && result?.rctiId) return `RCTI created (${result.rctiId})`;
            return step.label.replace('...', '') + ' — done';
        }
        if (status === 'failed') {
            return step.label.replace('...', '') + ' — failed';
        }
        if (status === 'skipped') {
            return step.label.replace('...', '') + ' — skipped';
        }
        return step.label;
    };

    return (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
            <div className="bg-white rounded-3xl shadow-2xl ring-1 ring-gray-900/5 p-6 max-w-md w-full mx-4">
                <h3 className="text-xl font-bold text-gray-800 mb-6">
                    {isDone
                        ? (hasErrors ? 'Completed with errors' : 'Job completed successfully')
                        : 'Completing job...'}
                </h3>

                {/* Stepper */}
                <div className="space-y-4 mb-6">
                    {STEPS.map((step, index) => {
                        const status = getStepStatus(step.key);
                        return (
                            <div key={step.key} className="flex items-start space-x-3">
                                {/* Icon */}
                                <div className="flex-shrink-0 w-6 h-6 flex items-center justify-center mt-0.5">
                                    {getStepIcon(step.key)}
                                </div>

                                {/* Label + error detail */}
                                <div className="flex-1">
                                    <p className={`text-sm ${getStepTextClass(step.key)}`}>
                                        {getStepLabel(step)}
                                    </p>
                                    {status === 'failed' && stepResults[step.key]?.message && (
                                        <p className="text-xs text-red-500 mt-1">
                                            {stepResults[step.key].message}
                                        </p>
                                    )}
                                </div>

                                {/* Connector line (not for last step) */}
                                {index < STEPS.length - 1 && (
                                    <div className="hidden" /> // Connector handled by spacing
                                )}
                            </div>
                        );
                    })}
                </div>

                {/* Error summary */}
                {isDone && hasErrors && (
                    <div className="mb-4 p-3 bg-orange-50 border border-orange-200 rounded">
                        <p className="text-sm text-orange-700">
                            <FaExclamationTriangle className="inline mr-1" />
                            Some steps failed. You can manually create the invoice and/or RCTI using the "Create Invoice & RCTI" button on this page.
                        </p>
                    </div>
                )}

                {isDone && warnings.length > 0 && (
                    <div className="mb-4 p-3 bg-yellow-50 border border-yellow-200 rounded">
                        {warnings.map((warning, index) => (
                            <p key={`${warning.code || 'warning'}-${index}`} className="text-sm text-yellow-800">
                                <FaExclamationTriangle className="inline mr-1" />
                                {warning.message}
                            </p>
                        ))}
                    </div>
                )}

                {/* Close button - only when done */}
                {isDone && (
                    <div className="flex justify-end">
                        <button
                            type="button"
                            onClick={onClose}
                            className="px-4 py-2 bg-green-600 text-white rounded hover:bg-green-600-dark transition-colors"
                        >
                            Close
                        </button>
                    </div>
                )}
            </div>
        </div>
    );
};

export default CompletionProgressModal;
